import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Plug,
  Users,
  ScrollText,
  LogOut,
  KeyRound,
  Zap,
  BadgePercent,
  UserCircle,
  ShieldCheck,
  Calculator,
  BookOpen,
  LifeBuoy,
  Megaphone,
  Barcode,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { useActiveBling, blingLabel } from "../lib/blingContext";
import { Toaster } from "./ui/sonner";
import TrialBanner from "./TrialBanner";
import AnnouncementBanners from "./AnnouncementBanners";
import { Store, ChevronDown } from "lucide-react";

const NAV = [
  { to: "/catalogo", label: "Catálogo de Produtos", icon: BookOpen, testid: "nav-catalog" },
  { to: "/precificacao", label: "Precificação", icon: Calculator, testid: "nav-pricing" },
  { to: "/bling-discount", label: "Faturamento", icon: BadgePercent, testid: "nav-bling-discount", roles: ["admin", "account_admin", "platform_admin", "supervisor", "faturamento"] },
  { to: "/zpl-converter", label: "Conversor ZPL", icon: Zap, testid: "nav-zpl" },
  { to: "/ean-generator", label: "Gerador de EAN", icon: Barcode, testid: "nav-ean" },
  { to: "/integrations", label: "Integrações", icon: Plug, testid: "nav-integrations" },
  { to: "/support", label: "Suporte", icon: LifeBuoy, testid: "nav-support" },
  { to: "/minha-conta", label: "Minha Conta", icon: UserCircle, testid: "nav-my-account" },
  // ---- Platform admin only (Vyntric operator) ----
  { to: "/admin", label: "Painel Admin", icon: ShieldCheck, testid: "nav-admin", platformAdminOnly: true },
  { to: "/admin/support", label: "Suporte (Admin)", icon: LifeBuoy, testid: "nav-admin-support", platformAdminOnly: true },
  { to: "/admin/announcements", label: "Avisos (Admin)", icon: Megaphone, testid: "nav-admin-announcements", platformAdminOnly: true },
  { to: "/users", label: "Usuários", icon: Users, testid: "nav-users", platformAdminOnly: true },
  { to: "/platform-settings", label: "Credenciais Plataforma", icon: KeyRound, testid: "nav-platform-settings", platformAdminOnly: true },
  { to: "/instrucoes-bling", label: "Manual Bling (público)", icon: BookOpen, testid: "nav-bling-instructions", platformAdminOnly: true },
  { to: "/audit-logs", label: "Logs & Auditoria", icon: ScrollText, testid: "nav-logs", platformAdminOnly: true },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();
  const { blings, activeBling, activeId, setActiveId } = useActiveBling();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-white" data-testid="app-shell">
      {/* Sidebar */}
      <aside className="w-64 border-r border-slate-200 bg-slate-50 flex flex-col">
        <div className="h-16 px-5 flex items-center border-b border-slate-200">
          <div className="flex items-center gap-2.5">
            <img
              src="/vyntric-favicon.png"
              alt="Vyntric"
              className="w-9 h-9 object-contain"
              data-testid="sidebar-logo"
            />
            <div className="leading-tight">
              <div className="text-sm font-black tracking-tight">Vyntric</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">
                Multimarketplace
              </div>
            </div>
          </div>
        </div>
        {/* Global Bling selector — every Bling-aware page (Faturamento,
            Catálogo, Regras, Preço, Dashboard) filters by this choice.
            Only shown when the tenant has at least one connected Bling. */}
        {blings.length > 0 && (
          <div className="px-3 pt-3 pb-2 border-b border-slate-200" data-testid="global-bling-selector">
            <div className="text-[9px] uppercase tracking-widest font-bold text-slate-500 mb-1.5 flex items-center gap-1.5">
              <Store className="w-3 h-3" strokeWidth={2.5} />
              Loja ativa
            </div>
            {blings.length === 1 ? (
              <div
                className="w-full px-2.5 py-2 text-xs font-bold text-slate-900 bg-white border border-slate-300 truncate"
                data-testid="global-bling-single"
                title={blingLabel(blings[0])}
              >
                {blingLabel(blings[0])}
              </div>
            ) : (
              <div className="relative">
                <select
                  value={activeId || ""}
                  onChange={(e) => setActiveId(e.target.value)}
                  className="w-full appearance-none pl-2.5 pr-8 py-2 text-xs font-bold text-slate-900 bg-white border border-slate-300 hover:border-slate-900 focus:border-[#002EA6] focus:outline-none cursor-pointer truncate"
                  data-testid="global-bling-select"
                  title={activeBling ? blingLabel(activeBling) : ""}
                >
                  {blings.map((b) => (
                    <option key={b.id} value={b.id}>{blingLabel(b)}</option>
                  ))}
                </select>
                <ChevronDown className="w-3.5 h-3.5 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500" />
              </div>
            )}
            {blings.length > 1 && (
              <div className="text-[10px] text-slate-500 mt-1 leading-tight" data-testid="global-bling-hint">
                Filtra pedidos, regras e catálogo por esta conta.
              </div>
            )}
          </div>
        )}
        <nav className="flex-1 px-2 py-4 space-y-0.5 overflow-y-auto">
          {NAV
            .filter((n) => !n.platformAdminOnly || user?.role === "platform_admin")
            .filter((n) => !n.roles || n.roles.includes(user?.role))
            .map(({ to, label, icon: Icon, testid }) => {
            const active = loc.pathname === to;
            return (
              <Link
                key={to}
                to={to}
                data-testid={testid}
                className={`flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                  active
                    ? "bg-[#002EA6] text-white font-semibold"
                    : "text-slate-700 hover:bg-slate-200/60 hover:text-slate-900"
                }`}
              >
                <Icon className="w-4 h-4" strokeWidth={2.25} />
                {label}
              </Link>
            );
          })}
        </nav>
        <div className="border-t border-slate-200 p-3">
          <div className="px-2 py-1 text-xs">
            <div className="font-semibold text-slate-900 truncate" data-testid="user-name">
              {user?.name}
            </div>
            <div className="text-slate-500 truncate font-mono">{user?.email}</div>
            <div className="mt-1 inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest bg-slate-900 text-white">
              {user?.role}
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-wider border border-slate-300 hover:border-slate-900 hover:bg-slate-900 hover:text-white transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" /> Sair
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col">
        <header className="h-16 border-b border-slate-200 bg-white sticky top-0 z-30 flex items-center justify-between px-6">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">
              Vyntric · Logístico, Fiscal e Operacional
            </div>
            <div className="text-sm font-black tracking-tight">
              {NAV.find((n) => n.to === loc.pathname)?.label || "Painel"}
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span className="font-mono">v1.0</span>
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
            <span>Sistema operacional</span>
          </div>
        </header>
        <TrialBanner />
        <div className="flex-1 px-6 py-6 max-w-[1600px] w-full mx-auto">
          <AnnouncementBanners />
          {children}
        </div>
      </main>
      <Toaster position="top-right" richColors />
    </div>
  );
}
